 import { GetUsageDataQuery } from '../../../gql/graphql';

interface UsageMetricsQuery {
  usageMetrics: Array<{
    name: string;
    displayName: string;
    totalValue: number;
    subscriptions: Array<{
      subscriptionId: string;
      granularValues: Array<{
        startDate: string;
        endDate: string;
        value: number;
      }>;
    }>;
  }>;
}

export interface ProcessedUsageData {
  totalRequests: string;
  contentRequests: {
    uncached: { value: number; total: number };
    cached: { value: number; total: number };
  };
  mediaRequests: {
    uncached: { value: number; total: number };
    cached: { value: number; total: number };
  };
}

export interface ProcessedSitecoreAIMetrics {
  totalRequests: string;
}

export const processUsageData = (data: GetUsageDataQuery | undefined): ProcessedUsageData => {
  const defaultData: ProcessedUsageData = {
    totalRequests: '0',
    contentRequests: {
      uncached: { value: 0, total: 0 },
      cached: { value: 0, total: 0 }
    },
    mediaRequests: {
      uncached: { value: 0, total: 0 },
      cached: { value: 0, total: 0 }
    }
  };

  if (!data?.allSubscriptions?.length) {
    return defaultData;
  }

  const subscriptions = data.allSubscriptions;
  const subscription = subscriptions[0];
  
  if (!subscription?.entitlements?.length) {
    return defaultData;
  }

  const entitlements = subscription.entitlements;

  // Find specific metric entitlements by their exact metric names
  const cachedMediaEnt = entitlements.find(ent => ent.metric === 'xmcloud-edgecachedmediarequests');
  const uncachedMediaEnt = entitlements.find(ent => ent.metric === 'xmcloud-edgeuncachedmediarequests');
  const cachedContentEnt = entitlements.find(ent => ent.metric === 'xmcloud-edgecachedcontentrequests');
  const uncachedContentEnt = entitlements.find(ent => ent.metric === 'xmcloud-edgeuncachedcontentrequests');

  // Calculate total requests: sum of all 4 metrics' selectedPeriod.total values
  const totalRequests = [cachedMediaEnt, uncachedMediaEnt, cachedContentEnt, uncachedContentEnt]
    .reduce((sum, ent) => {
      const total = ent?.consumption?.selectedPeriod?.total || 0;
      return sum + Number(total);
    }, 0);

  // Content requests data: currentPeriod.value / quantity
  const contentRequests = {
    uncached: {
      value: Number(uncachedContentEnt?.consumption?.currentPeriod?.value || 0),
      total: Number(uncachedContentEnt?.quantity || 0)
    },
    cached: {
      value: Number(cachedContentEnt?.consumption?.currentPeriod?.value || 0),
      total: Number(cachedContentEnt?.quantity || 0)
    }
  };

  // Media requests data: currentPeriod.value / quantity  
  const mediaRequests = {
    uncached: {
      value: Number(uncachedMediaEnt?.consumption?.currentPeriod?.value || 0),
      total: Number(uncachedMediaEnt?.quantity || 0)
    },
    cached: {
      value: Number(cachedMediaEnt?.consumption?.currentPeriod?.value || 0),
      total: Number(cachedMediaEnt?.quantity || 0)
    }
  };

  return {
    totalRequests: totalRequests.toLocaleString(),
    contentRequests: contentRequests,
    mediaRequests: mediaRequests
  };
};

export const processSitecoreAIMetrics = (data: UsageMetricsQuery | undefined): ProcessedSitecoreAIMetrics => {
  const xmCloudVisits = data?.usageMetrics?.find(m => m.name === 'xmcloud-visits');

  return {
    totalRequests: xmCloudVisits?.totalValue.toLocaleString() || '0'
  };
};

export const processUsageMetrics = (data: UsageMetricsQuery | undefined): ProcessedUsageData => {
  const defaultData: ProcessedUsageData = {
    totalRequests: '0',
    contentRequests: {
      uncached: { value: 0, total: 0 },
      cached: { value: 0, total: 0 }
    },
    mediaRequests: {
      uncached: { value: 0, total: 0 },
      cached: { value: 0, total: 0 }
    }
  };

  if (!data?.usageMetrics?.length) {
    return defaultData;
  }

  const metrics = data.usageMetrics;

  // Find metrics by their exact names
  const cachedMediaMetric = metrics.find(m => m.name === 'xmcloud-edgecachedmediarequests');
  const uncachedMediaMetric = metrics.find(m => m.name === 'xmcloud-edgeuncachedmediarequests'); 
  const cachedContentMetric = metrics.find(m => m.name === 'xmcloud-edgecachedcontentrequests');
  const uncachedContentMetric = metrics.find(m => m.name === 'xmcloud-edgeuncachedcontentrequests');

  // Calculate total requests: sum of all 4 metrics' totalValues
  const totalRequests = [cachedMediaMetric, uncachedMediaMetric, cachedContentMetric, uncachedContentMetric]
    .reduce((sum, metric) => {
      return sum + (metric?.totalValue || 0);
    }, 0);

  // Extract individual metric values (using totalValue for filtered date range)
  const contentRequests = {
    uncached: {
      value: uncachedContentMetric?.totalValue || 0,
      total: 0
    },
    cached: {
      value: cachedContentMetric?.totalValue || 0, 
      total: 0
    }
  };

  const mediaRequests = {
    uncached: {
      value: uncachedMediaMetric?.totalValue || 0,
      total: 0 
    },
    cached: {
      value: cachedMediaMetric?.totalValue || 0,
      total: 0
    }
  };

  return {
    totalRequests: totalRequests.toLocaleString(),
    contentRequests: contentRequests,
    mediaRequests: mediaRequests
  };
};

const formatDateAsUTC = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  const utcDate = new Date(Date.UTC(year, month, day));
  return utcDate.toISOString().split('T')[0]; // "2025-09-20"
};

// Helper function to convert DatePickerValue to RequestsUsageQueryInput
export const datePickerToQueryInput = (datePickerValue: any) => {
  let queryInput: any = {};

  let startDate: Date | null = null;
  let endDate: Date | null = null;
  
  if (datePickerValue.view === 'Monthly' && datePickerValue.currentMonth) {
    const date = datePickerValue.currentMonth;
    const year = date.getFullYear();
    const month = date.getMonth();
    
    startDate = new Date(year, month, 1);
    endDate = new Date(year, month + 1, 0);
  } else if (datePickerValue.view === 'Daily' && datePickerValue.singleDate) {
    startDate = datePickerValue.singleDate;
    endDate = datePickerValue.singleDate;
  } else if (datePickerValue.view === 'Custom' && datePickerValue.dateRange) {
    const [customStart, customEnd] = datePickerValue.dateRange;
    if (customStart && customEnd) {
      startDate = customStart;
      endDate = customEnd;
    }
  }
  
  if (startDate && endDate) {
    queryInput = {
      dateRange: {
        startDate: formatDateAsUTC(startDate),
        endDate: formatDateAsUTC(endDate)
      },
    };
  }

  return queryInput;
};
